import type { library } from '@cwos/api-client';
import type { PillVariant } from './libraryLogic.js';

/**
 * Pure view logic for WHAT APPLIES HERE — the resolver's answer, in words.
 *
 * `GET /knowledge/applicable` is the route that makes the Operational Library's
 * catalog and applicability code (O15, 2,031 LOC with no API and no tool)
 * visible to a customer for the first time. It is also the single most
 * dangerous thing on this surface to render carelessly, for two reasons.
 *
 * ===========================================================================
 * 1 · "NOTHING CAME BACK" IS NOT AN ANSWER, AND THE REJECTIONS ARE HALF OF IT
 * ===========================================================================
 * The resolver KEEPS the candidates that did not apply, with the dimension each
 * failed on, precisely so a compliance officer is told "we considered the
 * Minnesota rule and here is why it does not apply to you". A screen that
 * dropped `rejected` would turn a reasoned answer into a silence, and a silence
 * on this surface reads as "you are not subject to anything".
 *
 * One rejection reason is not a rejection at all: `invalid_context_effective_date`
 * means the `at` we sent was not a date, so NOTHING WAS COMPUTED. Rendering it
 * beside "jurisdiction did not match" would file a failure to ask under
 * "asked and answered". {@link isComputationFailure} separates them.
 *
 * ===========================================================================
 * 2 · "CURRENT" MUST NOT READ AS "ALWAYS WAS"
 * ===========================================================================
 * The resolver is time-aware: a rule in force in June and superseded in August
 * is the answer to a June question and not to today's. So the screen always
 * states the date it asked about, and {@link asOfStanding} makes a historical
 * question say so in its own words rather than letting today's framing carry a
 * past answer.
 */

// ---------------------------------------------------------------------------
// The role a rule played in the answer
// ---------------------------------------------------------------------------

export function contributingRoleLabel(role: library.ContributingRole | string): string {
  switch (role) {
    case 'governing':
      return 'The policy here';
    case 'external_floor':
      return 'An outside requirement';
    case 'advisory':
      return 'Advice, not a requirement';
    case 'superseded_by_more_specific':
      return 'Outranked by a more specific rule';
    case 'doctrine_default':
      return 'Phibian’s own default';
    default:
      return role;
  }
}

export function contributingRoleSentence(role: library.ContributingRole | string): string {
  switch (role) {
    case 'governing':
      return 'The most specific policy your organization has that answers this question.';
    case 'external_floor':
      return 'Set outside your organization and binding. Your own policy may be stricter than this and may never be looser.';
    case 'advisory':
      return 'Outside material that does not bind you. “The manual suggests” and “the statute requires” are not the same sentence.';
    case 'superseded_by_more_specific':
      return 'This does apply to you, and a narrower rule answers the same question — so the narrower one governs. Kept because which rule lost, and to what, is part of the explanation.';
    case 'doctrine_default':
      return 'Nothing in your Library or in outside requirements answered this, so Phibian fell back on its own default.';
    default:
      return 'Phibian does not recognise this role and is showing it as stored.';
  }
}

export function contributingRoleVariant(role: library.ContributingRole | string): PillVariant {
  switch (role) {
    case 'governing':
      return 'good';
    case 'external_floor':
      return 'urgent';
    case 'advisory':
      return 'sky';
    case 'superseded_by_more_specific':
      return 'plain';
    case 'doctrine_default':
      return 'unsure';
    default:
      return 'plain';
  }
}

// ---------------------------------------------------------------------------
// The requirement itself
// ---------------------------------------------------------------------------

/**
 * A machine-comparable requirement, as a sentence.
 *
 * `kind: 'statement'` carries no number and must NOT be rendered as one — a
 * missing `value` printed as `0` would turn "this is a rule you read" into
 * "this rule requires zero of something".
 */
export function requirementSentence(req: {
  kind: library.RuleRequirement['kind'] | string;
  value?: number;
  unit?: string;
}): string {
  const unit = req.unit === undefined || req.unit === '' ? '' : ` ${req.unit}`;
  const amount = req.value === undefined ? null : `${req.value}${unit}`;
  switch (req.kind) {
    case 'minimum':
      return amount === null ? 'A minimum, with no amount recorded.' : `At least ${amount}.`;
    case 'maximum':
      return amount === null ? 'A maximum, with no amount recorded.' : `No more than ${amount}.`;
    case 'prohibition':
      return 'Not permitted.';
    case 'obligation':
      return 'Required.';
    case 'statement':
      return 'Stated in words rather than as a number — read the rule.';
    default:
      return 'Phibian does not recognise this kind of requirement and is showing the rule as stored.';
  }
}

// ---------------------------------------------------------------------------
// Why a candidate did not apply
// ---------------------------------------------------------------------------

/**
 * Reasons that mean NOTHING WAS COMPUTED, as opposed to "computed, and it does
 * not apply to you".
 *
 * A malformed date on either side stops the matcher before it decides anything.
 * Grouping these with the mismatches would let "you sent a date I cannot read"
 * be read as "no rules apply to you", which on this surface is a compliance
 * answer somebody acts on.
 */
const COMPUTATION_FAILURES: ReadonlySet<string> = new Set([
  'invalid_context_effective_date',
  'invalid_source_effective_from',
  'invalid_source_effective_to',
]);

export function isComputationFailure(reason: string): boolean {
  return COMPUTATION_FAILURES.has(reason);
}

export function anyComputationFailure(
  rejected: readonly { reasons: readonly string[] }[],
): boolean {
  return rejected.some((r) => r.reasons.some(isComputationFailure));
}

export function rejectionReasonLabel(reason: string): string {
  switch (reason) {
    // --- DECISIONS. The context said something, and it was not this. --------
    case 'tenant_mismatch':
      return 'It belongs to another organization.';
    case 'tenant_not_declared_on_source':
      return 'This rule does not record which organization it belongs to, so Phibian will not reason over it. That is a fault in the record, not a decision about you.';
    case 'jurisdiction_mismatch':
      return 'It is written for a state or region you do not operate in.';
    case 'program_mismatch':
      return 'It is narrowed to programs other than the one you named.';
    case 'service_mismatch':
      return 'It is narrowed to services other than the one you named.';
    case 'location_mismatch':
      return 'It is narrowed to sites other than the one you named.';
    case 'payer_mismatch':
      return 'It is narrowed to payers other than the one you named.';
    case 'role_mismatch':
      return 'It is narrowed to roles other than the one you named.';
    case 'client_mismatch':
      return 'It is set for particular people, and not for the one you named.';

    // --- GAPS. Nobody said. These are NOT decisions, and they say so. -------
    case 'jurisdiction_not_recorded':
      return 'It is written for a particular state or region, and nowhere in your records says where you operate. Phibian cannot tell whether it governs you.';
    case 'jurisdiction_recorded_more_broadly':
      return 'It is written for a county or district inside somewhere you operate, and your records only go as far as the state. Operating in a state does not mean operating in every county in it — record the county to get an answer.';
    case 'jurisdiction_not_recognised':
      return 'Phibian does not recognise one of the region codes involved, so it cannot work out whether one contains the other. This is not a decision that the rule does not apply.';
    case 'program_not_recorded':
      return 'It is narrowed to particular programs, and you named no program. Phibian cannot tell whether it governs you.';
    case 'service_not_recorded':
      return 'It is narrowed to particular services, and you named no service. Phibian cannot tell whether it governs you.';
    case 'location_not_recorded':
      return 'It is narrowed to particular sites, and you named no site. Phibian cannot tell whether it governs you.';
    case 'payer_not_recorded':
      return 'It is narrowed to particular payers, and no payer relationship covers this question. Phibian cannot tell whether it governs you.';
    case 'role_not_recorded':
      return 'It is narrowed to particular roles, and you named no role. Phibian cannot tell whether it governs you.';
    case 'client_not_recorded':
      return 'It is set for particular people, and you named nobody. Phibian cannot tell whether it governs you.';

    case 'not_yet_effective':
      return 'It had not taken effect on the date you asked about.';
    case 'no_longer_effective':
      return 'It had already ended by the date you asked about.';
    case 'invalid_context_effective_date':
      return 'Phibian could not read the date you asked about, so nothing was worked out. This is not “no rules apply”.';
    case 'invalid_source_effective_from':
      return 'This rule’s own start date could not be read, so it was not considered. This is not a decision that it does not apply.';
    case 'invalid_source_effective_to':
      return 'This rule’s own end date could not be read, so it was not considered. This is not a decision that it does not apply.';
    default:
      // The rejection vocabulary lives in `intelligence/library/applicability.ts`
      // and can grow. An unfamiliar reason must read as an unfamiliar reason,
      // never as a blank line under a rule's name.
      return reason;
  }
}

/**
 * WHICH OF THE SIX STATES, IN WORDS.
 *
 * `does_not_apply` is the only one an operator may act on. The other three a
 * rejection can be in are gaps and dates, and a screen that gave them all the
 * same voice would be the server's old `${dimension}_mismatch_or_missing`
 * rebuilt in the UI.
 */
export function applicabilityStateLabel(state: string): string {
  switch (state) {
    case 'does_not_apply':
      return 'Does not apply to you';
    case 'unknown':
      return 'Could not be determined';
    case 'not_yet_effective':
      return 'Not yet in effect';
    case 'historical':
      return 'No longer in effect';
    case 'requires_reconciliation':
      return 'Needs reconciliation';
    case 'applies':
      return 'Applies';
    default:
      // An unfamiliar state must read as unfamiliar, never as a blank pill.
      return state;
  }
}

/**
 * `unsure` for every state that is NOT a decision, `plain` for the one that is.
 *
 * Colour is never the message here — the pill always carries its words — but a
 * screen that painted "could not be determined" the same as "does not apply to
 * you" would be reintroducing the collapse in the one channel an operator scans
 * fastest.
 */
export function applicabilityStateVariant(state: string): PillVariant {
  switch (state) {
    case 'applies':
      return 'good';
    case 'does_not_apply':
      return 'plain';
    case 'unknown':
      return 'unsure';
    case 'requires_reconciliation':
      return 'urgent';
    case 'not_yet_effective':
    case 'historical':
      return 'sky';
    default:
      return 'unsure';
  }
}

/**
 * WHICH CLASSIFICATION TO COMPLETE, in the words an operator would use to go
 * and complete it.
 *
 * The founder's sentence is "Applicability could not be determined because
 * provider/program/service classification is incomplete" — it NAMES the
 * incomplete classification, so a screen that said only "could not be
 * determined" would have moved the collapse one level up.
 */
export function undeterminedDimensionSentence(dimension: string, reason: string): string {
  if (reason === 'context_broader_than_requirement') {
    return 'Your operating regions are recorded more broadly than this requirement is written. Record the county or district if you operate there.';
  }
  if (reason === 'jurisdiction_not_recognised') {
    return 'A region code here is one Phibian does not recognise, so it cannot place it in the state/county hierarchy.';
  }
  if (reason === 'source_value_unreadable') {
    return 'A date on the requirement itself could not be read. Phibian published this one; report it rather than acting on it.';
  }
  if (reason === 'context_value_unreadable') {
    return 'The date you asked about could not be read, so nothing was worked out.';
  }
  switch (dimension) {
    case 'jurisdiction':
      return 'Nowhere in your records says which states or regions you operate in.';
    case 'program':
      return 'No program is recorded for this question.';
    case 'service':
      return 'No service is recorded for this question.';
    case 'location':
      return 'No site is recorded for this question.';
    case 'payer':
      return 'No verified payer relationship covers this question.';
    case 'role':
      return 'No worker role is recorded for this question.';
    case 'client':
      return 'No person is named for this question.';
    case 'tenant':
      return 'The organization on this record is missing.';
    case 'effective_date':
      return 'The dates involved could not be read.';
    default:
      return `${dimension} is not recorded for this question.`;
  }
}

/**
 * The heading for the routed-gap section. The ruling's own words, because they
 * are the sentence a compliance operator has to be able to repeat.
 */
export const UNDETERMINED_HEADING = 'Applicability could not be determined';

export const UNDETERMINED_NOTE =
  'These requirements might govern you and Phibian cannot tell, because something about how ' +
  'your organization is classified is not recorded. This is NOT “they do not apply” and it is ' +
  'not permission to ignore them. Complete the classification named under each, or send it to ' +
  'whoever owns that record.';

/**
 * The one sentence that says whether the context named a dimension at all.
 *
 * The reasons now split — `…_mismatch` is "you named a program and it was a
 * different one", `…_not_recorded` is "you named no program at all, and an
 * absent context field is never a wildcard". The second is by far the commoner
 * and the one an operator misreads, so the fail-closed rule behind it is still
 * said out loud rather than left to be inferred from a reason list.
 */
export const FAIL_CLOSED_NOTE =
  'A dimension you did not name is not a wildcard. If a rule is written for one program and you ' +
  'did not say which program you are asking about, it is set aside — deliberately, so an ' +
  'unanswered question never widens a rule.';

// ---------------------------------------------------------------------------
// As-of
// ---------------------------------------------------------------------------

export type AsOfStanding =
  | { kind: 'today'; sentence: string }
  | { kind: 'historical'; sentence: string }
  | { kind: 'future'; sentence: string };

/**
 * Whether the answer on screen is about today, about the past, or about a date
 * that has not arrived — and the sentence that says so.
 *
 * Both arguments are `YYYY-MM-DD` calendar days, compared lexicographically,
 * which for that format is the calendar comparison and involves no timezone.
 * Constructing a `Date` here is how a screen west of Greenwich starts calling
 * today yesterday.
 */
export function asOfStanding(at: string, today: string): AsOfStanding {
  if (at === today) {
    return {
      kind: 'today',
      sentence:
        'What applies today. A rule that was in force earlier and has since been replaced is not in this answer.',
    };
  }
  if (at < today) {
    return {
      kind: 'historical',
      sentence:
        'This is the answer as it stood on the date you asked about — not today’s answer. A rule ' +
        'that has been replaced since then still governs this question.',
    };
  }
  return {
    kind: 'future',
    sentence:
      'This is the answer for a date that has not arrived. It shows what will apply if nothing ' +
      'changes between now and then, which is not a promise that nothing will.',
  };
}

/** Today as `YYYY-MM-DD` in the viewer's own zone. The default `at`. */
export function todayISO(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = `${now.getMonth() + 1}`.padStart(2, '0');
  const d = `${now.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * The banner when internal policy was clamped by an outside requirement.
 *
 * This is the fact a compliance reviewer opens this screen to find, so it gets
 * its own sentence rather than being inferred from two pills sitting near each
 * other.
 */
export const CONSTRAINED_BY_EXTERNAL =
  'Your own policy would have been more permissive here, and an outside requirement floors it. ' +
  'What applies is the outside requirement.';

// ---------------------------------------------------------------------------
// Which states the answer was computed for, and whether it could be computed
// ---------------------------------------------------------------------------

export type JurisdictionStanding = {
  /**
   * `answered`   the resolver bound one or more jurisdictions and matched
   *              against them.
   * `widened`    it could not make the answer specific, so it bound the WIDE
   *              union — more outside requirements apply here, not fewer.
   * `unknown`    nobody has recorded where this organization operates. NOT
   *              "nothing applies": nothing could be worked out.
   * `refused`    the caller named a program, site or service they may not
   *              reach. A statement about the READER, never about the thing.
   */
  readonly kind: 'answered' | 'widened' | 'unknown' | 'refused';
  readonly heading: string;
  readonly sentence: string;
  /** The states matched against. Empty for `unknown` and `refused`. */
  readonly jurisdictions: readonly string[];
};

/**
 * THE JURISDICTION ENVELOPE, IN WORDS — and the reason it must be on screen.
 *
 * An external hard requirement is a FLOOR, and it is the one thing in this
 * product a customer may not configure away. It is matched on jurisdiction, so
 * a resolver that cannot say which jurisdictions bind this organization cannot
 * apply any floor at all — and the answer it returns is a perfectly ordinary
 * looking one with the floors quietly missing from it.
 *
 * The server already knows the difference and puts it on the envelope for
 * exactly this reason: `no_jurisdiction_recorded` and `context_refused` carry NO
 * jurisdictions, and a consumer reading only `subjects` cannot tell those apart
 * from "we looked everywhere and nothing applies". This screen rendered neither.
 * It renders both now, and it says which of the four it is looking at.
 *
 * `context_absent` is the subtle one and it is NOT an error. The organization
 * records jurisdictions at a scope the request did not narrow — the demo agency
 * runs one program under a Wisconsin licence and everything else under a
 * Minnesota one — so the resolver binds BOTH and tells the caller which
 * dimension would have made the answer specific. On this dimension, and only on
 * this one, failing closed means MORE requirements apply rather than fewer.
 */
export function jurisdictionStanding(
  jurisdiction: library.JurisdictionResolution,
): JurisdictionStanding {
  switch (jurisdiction.state) {
    case 'resolved':
      return {
        kind: 'answered',
        heading: 'Worked out for ' + jurisdiction.jurisdictions.join(', '),
        sentence:
          jurisdiction.basis === 'organization_default'
            ? 'Taken from the state on your organization record, because nothing more specific ' +
              'has been written down about where you operate. That field is a convenience, not a ' +
              'statement of which regulator you answer to — record your operating jurisdictions ' +
              'to make this an operational fact.'
            : 'Taken from where your organization has recorded that it operates, at the ' +
              (jurisdiction.via === 'org' ? 'organization' : jurisdiction.via) +
              ' level. A narrower record beats a wider one.',
        jurisdictions: jurisdiction.jurisdictions,
      };
    case 'context_absent':
      return {
        kind: 'widened',
        heading: 'Worked out for ' + jurisdiction.jurisdictions.join(', '),
        sentence:
          'Your organization operates in more than one place and this question did not say which ' +
          (jurisdiction.undecided.join(' or ') || 'part of it') +
          ' it is about — so every outside requirement that could apply is included. Naming one ' +
          'would narrow this. Nothing has been dropped.',
        jurisdictions: jurisdiction.jurisdictions,
      };
    case 'no_jurisdiction_recorded':
      return {
        kind: 'unknown',
        heading: 'Phibian cannot say which outside requirements apply to you',
        sentence:
          'Nobody has recorded where this organization operates, so there is nothing to match an ' +
          'outside requirement against. THIS IS NOT “no outside requirement applies to you” — it ' +
          'is “we could not work it out”. Record your operating jurisdictions and ask again.',
        jurisdictions: [],
      };
    case 'context_refused':
      return {
        kind: 'refused',
        heading: 'This question is outside what your account may ask about',
        sentence:
          'You named a ' +
          jurisdiction.dimension +
          ' your account does not reach, so nothing was worked out. This says what YOUR account ' +
          'may ask about and nothing whatever about whether that ' +
          jurisdiction.dimension +
          ' exists.',
        jurisdictions: [],
      };
    default:
      return {
        kind: 'unknown',
        heading: 'Phibian cannot say which outside requirements apply to you',
        sentence:
          'The server answered with a state this screen does not recognise, so nothing here can ' +
          'be read as a decision that no outside requirement applies to you.',
        jurisdictions: [],
      };
  }
}

// ---------------------------------------------------------------------------
// Contradictions
// ---------------------------------------------------------------------------

/**
 * THE POLICY THAT CANNOT STAND, in words.
 *
 * `constrainedByExternal` and this are different facts and the screen must not
 * blur them. A clamp is arithmetic — the organization asked for less than the
 * outside requirement and gets the outside number. THIS is a written policy
 * that says the opposite of something the organization cannot opt out of, so
 * the policy has no effect at all, and the person reading this screen is the
 * person who can go and change it.
 *
 * Named for the reason `resolve.ts` separated it out: an operator left to
 * wonder why their SOP is not being followed is an operator who will conclude
 * the software is wrong.
 */
export function reportableConflictSentence(reason: string): string {
  switch (reason) {
    case 'customer_policy_contradicts_absolute':
      return (
        'Your organization’s own approved policy says the opposite of an outside requirement you ' +
        'cannot opt out of. The outside requirement is what applies; your policy has no effect ' +
        'here and should be changed to match.'
      );
    default:
      return (
        'Phibian recorded a conflict of a kind this screen does not recognise, and is showing it ' +
        'rather than hiding it.'
      );
  }
}

/**
 * WHY TWO APPLICABLE REQUIREMENTS COULD NOT BE COMBINED, in words.
 *
 * THREE DIFFERENT FACTS, and the screen must not merge them. The first two are
 * statements about the RULES: they do not relate, and somebody has to decide
 * what the organization actually does. The third is a statement about the
 * RECORD: something could not be read, and the fix is in the library rather
 * than in a policy meeting. A reader sent to argue with a regulator about a
 * missing number has been given the wrong sentence.
 *
 * None of them is "we combined them and here is the number", which is what this
 * screen showed before the resolver compared units: a floor of two hours
 * against a policy of sixty minutes compared 2 against 60 and rendered the
 * loser confidently.
 *
 * `default` SHOWS rather than hides, for `reportableConflictSentence`'s reason.
 */
export function requirementConflictSentence(reason: string): string {
  switch (reason) {
    case 'incomparable_requirement_kinds':
      return (
        'Two rules that both apply here state different kinds of requirement — a limit against a ' +
        'flat prohibition, say — so there is no number that satisfies both. Phibian will not ' +
        'invent one; the outside requirement is shown as it stands and somebody has to decide.'
      );
    case 'incommensurable_requirement_units':
      return (
        'Two rules that both apply here are measured in units that do not convert — visits ' +
        'against hours, or a weekly amount against a monthly one. Phibian will not guess a ' +
        'conversion, so it cannot say which is stricter. Somebody has to decide, or the two rules ' +
        'have to be restated in the same terms.'
      );
    case 'unresolved_requirement_comparison':
      return (
        'Phibian could not read one of these requirements well enough to compare them — a unit it ' +
        'does not recognise, a unit on one rule and not the other, or a limit with no amount ' +
        'recorded. This is a gap in the Library rather than a disagreement between the rules, and ' +
        'nothing here has been decided.'
      );
    default:
      return (
        'Two rules that both apply here state requirements that could not be compared, for a ' +
        'reason this screen does not recognise. It is showing that rather than hiding it, and ' +
        'nothing has been decided for you.'
      );
  }
}

/**
 * YOUR POLICY AND THE OUTSIDE REQUIREMENT COINCIDE EXACTLY.
 *
 * Its own sentence because it is its own answer. "We meet it exactly" is not
 * "we exceed it" and it is not "nothing applies": an agency that is sitting
 * precisely on a statutory floor has no margin, and the person reading this is
 * the person who would want to know that before they revise the policy down by
 * five minutes.
 */
export const EQUIVALENT_TO_EXTERNAL =
  'Your own policy asks for exactly what the outside requirement asks for — no more and no less. ' +
  'There is no margin between them.';

/**
 * YOUR POLICY IS THE STRICTER ONE.
 *
 * Said out loud so that "an outside requirement applies here" is not read as
 * "an outside requirement overrode you". It applied, it was compared, and the
 * organization's own rule is what governs.
 */
export const TENANT_POLICY_STRICTER =
  'An outside requirement applies here and your own policy is the stricter of the two, so your ' +
  'policy is what governs. The outside requirement is listed below with the rest.';

/**
 * Two rules of EQUAL standing pulling opposite ways, which precedence cannot
 * separate. Handed back rather than settled: choosing quietly would be the
 * software deciding something no human has.
 */
export const EQUAL_STANDING_CONTRADICTION =
  'Two rules that both apply here say opposite things, and neither outranks the other. Phibian ' +
  'will not pick one for you — somebody has to decide which is right.';

/**
 * WHY THIS SCREEN HAS NO DISMISS, HIDE OR ACKNOWLEDGE CONTROL.
 *
 * The two-authority ruling of 2026-09-07 enumerates what a tenant may not do
 * with the external floor, and one clause is easy to miss: a tenant may not
 * SILENTLY SUPPRESS AN APPLICABLE ABSOLUTE REQUIREMENT. A "dismiss" affordance
 * on a compliance surface becomes exactly that the first time somebody uses it
 * to clear a banner they did not want to read, and it becomes it without anyone
 * deciding to build a suppression feature.
 *
 * So the domain has no such affordance and this constant is here to say that
 * the absence is a decision. There is no dismiss, no hide, no snooze and no
 * acknowledge on this screen; there is no route behind one; and
 * `applicable-external-floor.test.tsx` scans the rendered surface for one.
 *
 * A future requirement to let an operator record "we know, and here is what we
 * are doing about it" is legitimate — and it is an ANNOTATION that appears
 * beside the requirement, not a control that removes it from the answer.
 */
export const NO_SUPPRESSION_NOTE =
  'An outside requirement that applies to you cannot be dismissed or hidden from this answer. ' +
  'If your organization believes one does not apply, that is a change to where you record that ' +
  'you operate — not a change to what this screen shows you.';

// ---------------------------------------------------------------------------
// Version and supersession history
// ---------------------------------------------------------------------------

export interface VersionInChain {
  readonly version: library.ExternalRequirementVersion;
  /** True for the version that is in the answer, as opposed to its history. */
  readonly isCited: boolean;
}

/**
 * EVERY VERSION OF THE REQUIREMENT CITED, oldest first, with the cited one
 * marked.
 *
 * ---------------------------------------------------------------------------
 * WHY THE ANSWER CANNOT CARRY ITS OWN HISTORY
 * ---------------------------------------------------------------------------
 * The resolver returns what is in force at the instant asked about. A version
 * that was replaced two years ago is therefore CORRECTLY absent from a question
 * about today — and that is exactly why a screen built only on `contributing`
 * could say what the requirement is and could never say that it replaced
 * something. The chain arrives separately, on `Resolution.externalVersions`.
 *
 * ---------------------------------------------------------------------------
 * WALKED IN BOTH DIRECTIONS, AND `null` IS ABSENT
 * ---------------------------------------------------------------------------
 * From the cited version, backwards through `supersedesId` and forwards through
 * whatever names it. Both, because a HISTORICAL question cites an old version
 * and its reader needs to be told what replaced it — a screen that only walked
 * backwards would show a June answer as though it were still current.
 *
 * Ordering is the chain itself, not `effectiveFrom`: a null effective date is a
 * version whose start nobody recorded, and sorting on it would silently file
 * that version first. The links are what the publisher asserted; the dates are
 * a property that may be missing.
 */
export function versionChainFor(
  versions: readonly library.ExternalRequirementVersion[],
  citedRuleId: string,
): readonly VersionInChain[] {
  const byId = new Map(versions.map((v) => [v.id, v]));
  const cited = byId.get(citedRuleId);
  if (!cited) return [];

  const older: library.ExternalRequirementVersion[] = [];
  const seen = new Set<string>([cited.id]);
  let cursor = cited.supersedesId === null ? undefined : byId.get(cited.supersedesId);
  while (cursor && !seen.has(cursor.id)) {
    seen.add(cursor.id);
    older.unshift(cursor);
    cursor = cursor.supersedesId === null ? undefined : byId.get(cursor.supersedesId);
  }

  const newer: library.ExternalRequirementVersion[] = [];
  // A successor is a version that names this one. Deliberately tolerant of a
  // corpus that somehow holds two: both are shown rather than one silently
  // chosen, because a fork in a supersession chain is a corpus fault a reader
  // should see and not a thing to render an arbitrary half of.
  let frontier = [cited.id];
  while (frontier.length > 0) {
    const next = versions.filter(
      (v) => v.supersedesId !== null && frontier.includes(v.supersedesId) && !seen.has(v.id),
    );
    if (next.length === 0) break;
    for (const v of next) seen.add(v.id);
    newer.push(...next);
    frontier = next.map((v) => v.id);
  }

  return [
    ...older.map((version) => ({ version, isCited: false })),
    { version: cited, isCited: true },
    ...newer.map((version) => ({ version, isCited: false })),
  ];
}

/**
 * The publisher's edition string, or the honest absence of one.
 *
 * NEVER "v1". The two-authority ruling: "if a source provides no version label,
 * do not invent one — absence is preferable to false precision". A screen that
 * filled the gap with an ordinal would be putting a number in a compliance
 * record that no publisher ever assigned, and it would be indistinguishable
 * from a real first edition.
 */
export function versionLabelText(versionLabel: string | null): string {
  return versionLabel ?? 'The publisher gave this no edition name';
}

/** `active` | `superseded` | `retired`, as a sentence about a past version. */
export function versionStateLabel(state: string): string {
  switch (state) {
    case 'active':
      return 'In force';
    case 'superseded':
      return 'Replaced by a later version';
    case 'retired':
      return 'Withdrawn';
    default:
      return state;
  }
}

/**
 * THE HEADING THE PHIBIAN EXECUTION ENVELOPE SITS UNDER.
 *
 * ---------------------------------------------------------------------------
 * WHY IT IS A SEPARATE BLOCK AND NOT ANOTHER NOTE IN THE STACK ABOVE
 * ---------------------------------------------------------------------------
 * Platform-constraint ruling of 2026-09-07:
 *
 *   "A platform constraint may block an action even when policy says the
 *    organization should perform it, but the explanation must distinguish
 *    'the governing requirement says X' from 'Phibian cannot execute X
 *    automatically because platform safety constraint Y applies.'"
 *
 * Everything above this heading on the card answers what the ORGANIZATION must
 * do. Everything under it answers what PHIBIAN will do. Rendered as one more
 * `role="note"` in the same stack, the two questions read as one list of
 * things that are wrong with your policy, which is the merged explanation the
 * ruling forbids — and the failure is not cosmetic. An operator who reads
 * "Phibian will not do this automatically" as part of the compliance answer
 * concludes the requirement has been dealt with.
 */
export const EXECUTION_ENVELOPE_HEADING = 'What Phibian will do about it';

/**
 * The sentence that keeps the two halves apart when Phibian is blocked.
 *
 * IT NAMES THE OBLIGATION AS STILL STANDING, which is the whole point. The
 * per-constraint sentences the server sends say what Phibian will not do and
 * why; this says what that does NOT mean, once, at the top — because the
 * reader who needs it is the one who stopped reading after the refusal.
 */
export const EXECUTION_BLOCKED_NOTE =
  'Phibian will not carry this out automatically. That is a Phibian limit, not a change to ' +
  'the requirement above — whatever governs you still governs you, and somebody has to do it ' +
  'by hand.';

/**
 * Nothing in the envelope blocks. Said out loud rather than left as an empty
 * block, for the same reason `[]` is a measured empty on the wire: silence
 * here would read as "we did not look".
 */
export const EXECUTION_ADMISSIBLE_NOTE =
  'No Phibian limit stops Phibian acting here. That is not a statement that the action is ' +
  'permitted or required — the requirement above is what says that.';
